import React, { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import MoneyInput from '../../common/MoneyInput';
import OtherLivingCostFields from './OtherLivingCostFields';
import { Tooltip } from 'react-tooltip';
import { capturedAffordability, otherLivingExpenseTotal, sumMonthlyAmounts } from '../../../services/individualAffordability';

export const legacyIndividualAffordabilityGroups = [
  {
    title: 'Income',
    columns: 'col-12 col-md-6 col-xl-4',
    fields: [
      { name: 'financial.income.grossIncome', label: 'Monthly Gross Income',
        help: 'Total before deductions, including other income.' },
      { name: 'individual.monthlyNetIncome', id: 'individual-net-income', label: 'Monthly Net Income',
        help: 'Actual take-home pay after payroll deductions, excluding other income entered separately. Check the figures against the payslip and bank evidence; count each expense or repayment once, including any supported payroll adjustments already recorded.' },
      { name: 'financial.income.otherIncome', label: 'Other Monthly Income',
        help: 'Regular net income not included in monthly net income.' }
    ]
  },
  {
    title: 'Monthly expenses',
    columns: 'col-12 col-md-6 col-xl-4',
    fields: [
      { name: 'financial.expenses.rent', label: 'Rent', help: '' },
      { name: 'financial.household.education', label: 'School and Education Fees', help: '' },
      { name: 'financial.household.groceries', label: 'Groceries', help: '' },
      { name: 'financial.household.ratesAndTaxes', label: 'Rates, Water and Electricity', help: '' },
      { name: 'financial.household.telkomCell', label: 'Phone and Internet', help: '' },
      { name: 'financial.household.fuelTravel', label: 'Fuel and Transport', help: '' },
      { name: 'financial.household.insurance', label: 'Insurance', help: '' },
      { name: 'financial.otherLivingExpenses', label: 'Other Living Costs',
        help: 'Medical costs, clothing and other regular household costs not already entered above. Exclude debt repayments and maintenance entered below.' },
      { name: 'financial.maintenance', label: 'Monthly Maintenance Payments', help: '' },
      { name: 'financial.expenses.bondInstalment', label: 'Bond Repayments', help: '' },
      { name: 'financial.expenses.vehicleFinance', label: 'Vehicle Finance', help: '' },
      { name: 'financial.expenses.creditCard', label: 'Credit-card Repayments', help: '' },
      { name: 'financial.household.storeCards', label: 'Store-card Repayments', help: '' },
      { name: 'financial.expenses.personalLoan', label: 'Other Debt Repayments',
        help: 'Personal/student loans, overdrafts and called sureties.' }
    ]
  }
];

export default function LegacyIndividualAffordabilityFields({ includeIncome = true }: { includeIncome?: boolean } = {}) {
  const { control, setValue, formState: { errors } } = useFormContext();
  const net = useWatch({ control, name: 'individual.monthlyNetIncome' });
  const financial = useWatch({ control, name: 'financial' });
  const otherItems: { name: string; amount?: number }[] | undefined = financial?.otherLivingExpenseItems;
  const itemised = otherItems != null, otherTotal = otherLivingExpenseTotal(financial);
  const figures = capturedAffordability({ individual: { monthlyNetIncome: net }, financial });
  const living = figures.livingExpenses, debtTotal = figures.debtPayments;
  const expenses = sumMonthlyAmounts([living, figures.maintenance, debtTotal]);
  const income = sumMonthlyAmounts([figures.netIncome, figures.otherIncome]);
  const remaining = income !== undefined && expenses !== undefined ? Math.round((income - expenses) * 100) / 100 : undefined;
  useEffect(() => { setValue('financial.affordabilityCaptured', true); }, [setValue]);
  useEffect(() => { setValue('financial.income.netIncome', net); }, [net, setValue]);
  useEffect(() => { if (itemised) setValue('financial.otherLivingExpenses', otherTotal); }, [itemised, otherTotal, setValue]);
  useEffect(() => {
    setValue('financial.livingExpenses', living);
    setValue('individual.monthlyLivingExpenses', living);
  }, [living, setValue]);
  useEffect(() => { if (debtTotal !== undefined) setValue('financial.hasExistingCreditObligations', debtTotal > 0); }, [debtTotal, setValue]);

  return (
    <section className="form-section" aria-labelledby="individual-affordability-heading">
      <div className="d-flex align-items-baseline gap-2 mb-4">
        <h6 id="individual-affordability-heading" className="form-section-title mb-0">{includeIncome ? 'Affordability' : 'Expense items'}</h6>
        <button type="button" className="btn btn-link text-muted p-0"
          aria-label="Help for Affordability" data-tooltip-id="individual-affordability-help">
          <i className="fa fa-question-circle" aria-hidden="true" />
        </button>
        <Tooltip id="individual-affordability-help" place="top"
          content="Enter monthly amounts in rand. Enter 0 where an item does not apply."
          openEvents={{ mouseenter: true, focus: true, click: true }}
          globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
          className="text-wrap text-start" style={{ maxWidth: '20rem', zIndex: 1080 }} />
      </div>
      {legacyIndividualAffordabilityGroups.filter(group => includeIncome || group.title !== 'Income').map(group => (
        <fieldset key={group.title} className="mb-4">
          <legend className="h6 mb-3">{group.title}</legend>
          <div className="row g-3 align-items-end">
            {group.fields.map(field => {
              const id = 'id' in field ? field.id : `affordability-${field.name}`;
              const error = field.name.split('.').reduce<any>((value, key) => value?.[key], errors);
              return (
                <div className={group.columns} key={field.name}>
                  <div className="form-group">
                    <div className="d-flex align-items-baseline gap-2">
                      <label htmlFor={id}>{field.label} (R)</label>
                      {field.help && <>
                        <button type="button" className="btn btn-link text-muted p-0"
                          aria-label={`Help for ${field.label}`} data-tooltip-id={`${id}-help`}>
                          <i className="fa fa-question-circle" aria-hidden="true" />
                        </button>
                        <Tooltip id={`${id}-help`} place="top" content={field.help}
                          openEvents={{ mouseenter: true, focus: true, click: true }}
                          globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
                          className="text-wrap text-start" style={{ maxWidth: '20rem', zIndex: 1080 }} />
                      </>}
                    </div>
                    <Controller name={field.name} control={control} rules={{
                      required: `${field.label} is required`,
                      min: { value: 0, message: `${field.label} cannot be negative` }
                    }} render={({ field: input }) => <MoneyInput
                      name={input.name} ref={input.ref} value={input.value} onValueChange={input.onChange} onBlur={input.onBlur}
                      id={id}
                      readOnly={itemised && field.name === 'financial.otherLivingExpenses'}
                      min="0"
                      step="0.01"
                      className={`form-control ${error ? 'is-invalid' : ''}`}
                      aria-invalid={!!error}
                    />} />
                    {error && <div role="alert" className="invalid-feedback">{String(error.message)}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
      <OtherLivingCostFields />
      <div className="row g-3 border-top pt-3" aria-live="polite">
        <div className="col-12">
          <div className="small text-muted">Total declared monthly debt repayments</div>
          <strong>{debtTotal === undefined ? 'Complete all debt repayment items' : `R ${debtTotal.toFixed(2)}`}</strong>
        </div>
        <div className="col-12 col-md-6">
          <div className="small text-muted">Total monthly expenses</div>
          <strong>{expenses === undefined ? 'Complete all expense items' : `R ${expenses.toFixed(2)}`}</strong>
        </div>
        <div className="col-12 col-md-6">
          <div className="small text-muted">Income left after expenses</div>
          <strong className={remaining !== undefined && remaining < 0 ? 'text-danger' : ''}>
            {remaining === undefined ? 'Complete net income, other income and expenses' : `R ${remaining.toFixed(2)}`}
          </strong>
          <button type="button" className="btn btn-link text-muted p-0 ms-2" aria-label="Help for income left after expenses"
            data-tooltip-id="individual-remaining-help"><i className="fa fa-question-circle" aria-hidden="true" /></button>
          <Tooltip id="individual-remaining-help" place="top"
            content="Net income plus other income, less all entered costs, maintenance and debt repayments. This is before the new loan repayment. Affordability also applies the regulatory minimum living expenses and verified obligations, so the final lending capacity may be lower."
            openEvents={{ mouseenter: true, focus: true, click: true }} globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
            className="text-wrap text-start" style={{ maxWidth: '20rem', zIndex: 1080 }} />
        </div>
      </div>
    </section>
  );
}
